import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { sheet } from "@web/theme/theme";

export type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Sticky footer, usually the primary Button. */
  footer?: ReactNode;
  children: ReactNode;
  size?: "small" | "regular" | "large";
  /** Stable trigger identity when a responsive layout remounts the original opener. */
  returnFocusId?: string;
};

let openSheets = 0;
let previousOverflow = "";

/**
 * Slide-up sheet (SPEC §6.2): grab handle, title, scrollable body, sticky footer with safe-area padding.
 * Closes on backdrop tap or Escape. At ≥768px it becomes a centered panel.
 */
export function BottomSheet({ open, onClose, title, footer, children, size = "regular", returnFocusId }: BottomSheetProps) {
  const id = useId();
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (!open || !ref.current) return;
    const dialog = ref.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.showModal();
    if (openSheets++ === 0) previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      if (--openSheets === 0) document.body.style.overflow = previousOverflow;
      const returnTarget = (returnFocusId ? document.getElementById(returnFocusId) : null) ?? opener;
      if (returnTarget?.isConnected) returnTarget.focus({ preventScroll: true });
    };
  }, [open, returnFocusId]);
  if (!open) return null;
  return createPortal(
      <dialog ref={ref} aria-modal="true" aria-labelledby={id} data-size={size} className={`${sheet.panel} v365-dialog`}
        onKeyDown={(event) => {
          if (event.key !== "Tab") return;
          const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button, input, select, textarea, a[href], [tabindex]')].filter((element) => element.tabIndex >= 0 && !element.matches(":disabled") && element.getClientRects().length > 0);
          const first = controls[0], last = controls.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }}
        onCancel={(event) => { event.preventDefault(); onClose(); }}
        onClick={(event) => { if (event.target !== event.currentTarget) return; const box = event.currentTarget.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) onClose(); }}>
        <div className={sheet.handleWrap}>
          <div className={sheet.handle} />
        </div>
        <div className={`${sheet.header} v365-dialog-header`}>
          <div className="v365-dialog-spacer" />
          <h2 id={id} className={sheet.title}>
            {title}
          </h2>
          <button type="button" aria-label="Close" onClick={onClose} className={sheet.close}>
            <X aria-hidden size={22} />
          </button>
        </div>
        <div className={`${sheet.body} v365-dialog-body`}>{children}</div>
        {footer && (
          <div className={`${sheet.footer} v365-dialog-footer`}>
            <div className={sheet.footerInner}>{footer}</div>
          </div>
        )}
      </dialog>, document.body
  );
}
